import type { PartForStateMachine } from './types';

function getPartByteRange(
  index: number,
  fileSize: number,
  numOfParts: number,
  minPartSize: number
) {
  const partSize = Math.max(minPartSize, Math.ceil(fileSize / numOfParts));

  // Return the remaining bytes for the last/only part
  if (index === numOfParts - 1) {
    const lastPartSize = fileSize - partSize * (numOfParts - 1);
    return {
      offset: fileSize - lastPartSize,
      size: lastPartSize,
    };
  }

  // Return divided chunks for other parts
  return { offset: partSize * index, size: partSize };
}

/**
 * Given a list of upload URLs, split the file into the same number of parts.
 * @param {File} file - A `File` instance.
 * @param {string[]} uploadUrls - A list of target upload URLs.
 * @param {number} minPartSize - Minimum part size.
 * @returns {Part[]} - A list of parts each associated with its upload URL.
 */
export function getUploadParts(
  file: File,
  uploadUrls: string[],
  minPartSize: number
) {
  const numOfParts = uploadUrls.length;
  const minUploadSize = (numOfParts - 1) * minPartSize + 1;

  if (file.size && file.size < minUploadSize) {
    const msg = `The file must be at least ${minUploadSize} bytes for ${numOfParts} uploadUrls (this is ${file.size} bytes)`;
    throw new Error(msg);
  }

  return uploadUrls.map((url, index) => {
    const { offset, size } = getPartByteRange(
      index,
      file.size,
      numOfParts,
      minPartSize
    );
    const blob = file.slice(offset, offset + size, file.type);

    return {
      bytesSent: 0,
      blob,
      index,
      retryCount: 0,
      url,
    };
  });
}

/**
 * Given a list of upload URLs, split the file into the same number of parts.
 * @param {File} file - A `File` instance.
 * @param {string[]} uploadUrls - A list of target upload URLs.
 * @param {number} minPartSize - Minimum part size.
 * @returns {PartForStateMachine[]} - A list of parts each associated with its upload URL.
 */
export function getUploadPartsForStateMachine(
  file: File,
  uploadUrls: string[],
  minPartSize: number,
  totalPartCount?: number | undefined
): PartForStateMachine[] {
  const numOfParts = totalPartCount ?? uploadUrls.length;
  const minUploadSize = (numOfParts - 1) * minPartSize + 1;

  if (file.size && file.size < minUploadSize) {
    const msg = `The file must be at least ${minUploadSize} bytes for ${numOfParts} uploadUrls (this is ${file.size} bytes)`;
    throw new Error(msg);
  }

  // Create parts based on numOfParts
  const parts: PartForStateMachine[] = [];
  for (let i = 0; i < numOfParts; i += 1) {
    const { offset, size } = getPartByteRange(
      i,
      file.size,
      numOfParts,
      minPartSize
    );
    const blob = file.slice(offset, offset + size, file.type);

    // Use the URL if available, otherwise set to null
    const url = i < uploadUrls.length ? uploadUrls[i] : null;
    parts.push({
      bytesSent: 0,
      blob,
      index: i,
      retryCount: 0,
      url,
    });
  }
  return parts;
}
